<template>
  <div>
    <h4 class="h3-sub mb-4">Record History</h4>
    <div
      class="list-view-table fds container"
      v-if="transactions && transactions.length > 0">
      <div class="row px-4 no-gutters">
        <h6 class="col-7 label-table mb-0 text-uppercase pl-2">Action</h6>
        <h6 class="col-2 label-table mb-0 text-uppercase">Taken by</h6>
        <h6 class="col-3 label-table mb-0 text-uppercase">Date and time</h6>
      </div>
      <hr class="mb-2 mt-1">
      <div class="list-wrapper p-3">
        <div v-for="(transaction, transactionIdx) in transactions" :key="transactionIdx" class="entry-history-transaction container-fluid mb-2 py-3">
          <div class="row no-gutters top-card" data-toggle="collapse" :data-target="`#log-row-${transactionIdx}`" aria-expanded="false" aria-controls="show-more">
            <h5 class="col-7 d-flex align-items-center p-sm font-weight-bold m-0">{{getTransactionLabel(transaction)}}</h5>
            <h5 class="col-2 d-flex align-items-center p-sm font-weight-bold m-0">{{transaction.initiator_name}}</h5>
            <div class=" col-3 d-flex justify-content-between align-items-center">
              <h5 class="p-sm font-weight-bold m-0">{{formatDate(transaction.c_at)}}</h5>
              <button v-if="hasLineItems(transaction)"
                      :data-target="`#log-row-${transactionIdx}`"
                      aria-controls="show-more"
                      aria-expanded="false"
                      class="btn-icon collapse-btn button-arrow px-1 mb-2"
                      data-toggle="collapse"
              >
                <icon class="arrow-chevron down"></icon>
              </button>
            </div>
          </div>
          <div v-if="hasLineItems(transaction)" class="collapse" :id="`log-row-${transactionIdx}`">
            <hr class="mt-3 mb-4"/>
            <div v-for="(lineItem, lineItemIdx) in transaction.entry_history_transaction_line_items" :key="lineItemIdx" class="container px-0">
              <!-- When a new record is created -->
              <div v-if="lineItem.old_value === lineItem.new_value" class="row no-gutters">
                <p class="col-3 desc pr-2 text-break">{{ lineItem.label_title }}</p>
                <p class="col-2 desc pr-2">changed to</p> 
                <p class="col-3 desc pr-2 text-break font-weight-bold">{{ lineItem.new_value }}</p> 
              </div>
              <!-- When making a change to a record -->
              <div v-else class="row no-gutters" >
                <p class="col-3 desc pr-2 text-break">{{ lineItem.label_title }}</p>
                <p class="col-2 desc pr-2">changed from</p>
                <p class="col-2 desc text-break pr-2">{{ lineItem.old_value }}</p>
                <p class="col-2 desc pr-2">to</p>
                <p class="col-3 desc pr-2 text-break font-weight-bold">{{ lineItem.new_value }}</p>     
              </div>
            </div>
          </div>  
        </div>
      </div>
        <div class="w-100 text-center">
          <div v-if="fullyLoaded">You've reached the end of the list</div>
          <button v-else class="btn blue compress" @click="loadTransactions" :disabled="viewMoreDisabled">
            <template v-if="busy">
              <xpinner :has-background="false" color="White" speed="fast"/>
            </template>
            View More Logs
          </button>
        </div>
    </div>
    <div v-else class="fds container">
      <h5 class="row font-weight-bold">No data has been found.</h5>
    </div>
  </div>
</template>

<script lang="ts">
  import Vue from "vue";
  import DatetimeHelper from "~/helpers/datetimeHelper";
  import {ObjectModelActionType} from "~/enums/objectModelActionType";
  import {ObjectModelDefaultActionType} from "~/enums/objectModelDefaultActionType";
  import {IEntryHistoryTransaction} from "~/interfaces/objectModelInterfaces";
  import {ModelMappingRules} from "../../mappingBuilder/enums/model-mapping-rules";
  import ContentElementComponent from "./content-element-component.vue";
  import RefreshEntryDataBus from '../buses/refreshEntryDataBus';

  export default Vue.extend({
    extends: ContentElementComponent,
    beforeMount() {
      if (this.$route.name === "detailView" || this.$route.name === "detailViewEditMode") {
        this.registerEventBusListeners();
        this.loadTransactions();
      }
    },
    data: () => {
      return {
        transactions: [],
        busy: false,
        fullyLoaded: false,
        fetchIdx: 0,
        initialFetch: true
      }
    },
    computed: {
      viewMoreDisabled() {
        return this.busy || this.fullyLoaded;
      }
    },
    methods: {
      registerEventBusListeners(): void {
        RefreshEntryDataBus.onRefresh(() => {
          this.fullyLoaded = false;
          this.transactions = [];
          this.initialFetch = true;
          this.fetchIdx = 0;
          this.loadTransactions();
        })
      },
      loadTransactions() {
        if (this.viewMoreDisabled) return;
        this.busy = true;

        this.$store.dispatch('detailViewStore/fetchHistory', {
          organizationId: this.selectedOrganization.id,
          detailViewId: this.$route.params.id || this.$route.params.detail_view_id,
          entryId: this.$route.params.entry_id,
          modelHistoryId: this.contentElement.id,
          fetchIdx: this.fetchIdx,
          initialFetch: this.initialFetch
        }).then(({transactions, fully_loaded}) => {
          if (fully_loaded) this.fullyLoaded = true;
          this.transactions.push(...transactions);
          this.fetchIdx += 10;
          if (this.initialFetch) {
            // accounts for the fact that the first fetch can yield up to 15 transactions
            this.fetchIdx += 5;
            this.initialFetch = false;
          }
          this.busy = false;
        });
      },
      formatDate(date: string) {
        return DatetimeHelper.toTimeString(date);
      },
      getTransactionLabel(transaction: IEntryHistoryTransaction): string {
        return transaction.action_type ? this.labelFromAction(transaction) : this.labelFromMapping(transaction);
      },
      labelFromAction(transaction: IEntryHistoryTransaction): string {
        switch(transaction.action_type) {
          case ObjectModelActionType.FillForm:
            return `Submission of ${transaction.linked_form_title}`;
          case ObjectModelActionType.ChangeFieldValues:
          case ObjectModelActionType.CustomNotification:
          case ObjectModelActionType.RunProcess:
          case ObjectModelActionType.ViewDocument:
          case ObjectModelActionType.LinkToAccount:
          case ObjectModelDefaultActionType.Edit:
            return `Action '${transaction.action_name}' has been taken`;
          case ObjectModelDefaultActionType.Create:
            return "Creation";
          default:
            return "Record Update";
        }
      },
      labelFromMapping(transaction: IEntryHistoryTransaction): string {
        switch (transaction.mapping_rule_type) {
          case ModelMappingRules.CreateNew:
            return `Created by ${transaction.mapping_source_title}`;
          case ModelMappingRules.UpdateCreate:
            return `Updates from ${transaction.mapping_source_title}`;
          default:
            return "Record Update";
        }
      },
      hasLineItems(transaction: IEntryHistoryTransaction): boolean {
        return transaction.entry_history_transaction_line_items && transaction.entry_history_transaction_line_items.length > 0
      },
    }
  });
</script>

